import { Link } from "react-router-dom";
import AgeGroupSelector from "~/components/AgeGroupSelector";
import stories from "~/stories";

export default function HomePage() {
  // Pick a featured story (rotate based on day of year)
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  const featured = stories[dayOfYear % stories.length];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
        <div className="absolute inset-0">
          {/* Floating decorative elements */}
          <svg className="absolute top-10 left-10 w-16 h-16 text-white/10 animate-bounce" style={{ animationDuration: "3s" }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <svg className="absolute top-32 right-20 w-12 h-12 text-white/10 animate-bounce" style={{ animationDuration: "4s", animationDelay: "1s" }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <svg className="absolute bottom-20 left-1/3 w-10 h-10 text-white/10 animate-bounce" style={{ animationDuration: "5s", animationDelay: "2s" }} viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="10" />
          </svg>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-24 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Wisdom Whispers
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8 max-w-2xl mx-auto">
            Beautiful bedtime stories that spark curiosity, teach history, and inspire dreams
          </p>
          <p className="text-sm opacity-75 mb-12">
            {stories.length} stories across 4 age groups &middot; Interactive illustrations &middot; Educational adventures
          </p>
        </div>
      </div>

      {/* Age Group Selector */}
      <div className="max-w-6xl mx-auto px-6 -mt-12 relative z-10">
        <AgeGroupSelector />
      </div>

      {/* Story of the Day */}
      {featured && (
        <div className="max-w-6xl mx-auto px-6 mt-16">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Story of the Day</h2>
          <Link
            to={`/age/${featured.ageGroup}/${featured.slug}`}
            className="block bg-gradient-to-r from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-200 text-amber-800 mb-3 inline-block">
                  Ages {featured.ageGroup}
                </span>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{featured.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">{featured.description}</p>
                <p className="text-sm text-amber-700 dark:text-amber-300 italic">"{featured.moral}"</p>
              </div>
              <div className="text-4xl ml-4">&#x1F4D6;</div>
            </div>
          </Link>
        </div>
      )}

      {/* Browse by Category */}
      <div className="max-w-6xl mx-auto px-6 mt-16 mb-16">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Browse by Topic</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { cat: "historical-figures", label: "Historical Figures", emoji: "&#x1F451;" },
            { cat: "world-history", label: "World History", emoji: "&#x1F30D;" },
            { cat: "science-nature", label: "Science & Nature", emoji: "&#x1F33F;" },
            { cat: "space-astronomy", label: "Space", emoji: "&#x2B50;" },
            { cat: "inventions-technology", label: "Inventions", emoji: "&#x1F4A1;" },
            { cat: "geography-cultures", label: "Geography", emoji: "&#x1F5FA;" },
            { cat: "arts-music", label: "Arts & Music", emoji: "&#x1F3B5;" },
            { cat: "life-skills", label: "Life Skills", emoji: "&#x2764;" },
            { cat: "math-logic", label: "Math & Logic", emoji: "&#x1F9E9;" },
            { cat: "health-body", label: "Health", emoji: "&#x1F34E;" },
          ].map(({ cat, label, emoji }) => (
            <Link
              key={cat}
              to={`/category/${cat}`}
              className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-center"
            >
              <span className="text-3xl mb-2" dangerouslySetInnerHTML={{ __html: emoji }} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
